import { REGEX_VALID_URL, REGEX_VALID_URL_CHARACTERS } from '../constants'
import { LuArrowUpRight, LuFolder, LuImage, LuTerminal, LuTrash } from 'react-icons/lu'
import { useState } from 'react'
import { Tooltip } from '@mui/material'
import DeleteConfirm from './DeleteConfirm'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ToggleButton from '@mui/material/ToggleButton'
import FormControlLabel from '@mui/material/FormControlLabel'
import CollapseSection from './CollapseSection'
import ColorSwitch from './ColorSwitch'
import TextFieldForm from './TextFieldForm'
import IconAgent from './IconAgent'
import useNodeMetadata from '../models/useNodeMetadata'
import { useOrgRole } from '../hooks/useOrgRole'
import type { ServiceFormData } from '../utilities/routerWrites'
import Alert from '@mui/material/Alert'

export type { ServiceFormData }

interface ServiceFormProps {
  nodeId?: string
  nodeName?: string
  title: string
  serviceType: ServiceType
  hideTypeChange: boolean
  onTypeChange?: (type: ServiceType) => void
  serviceUrl?: string
  initialData?: {
    title?: string | null
    url?: string | null
    command?: string | null
    admin?: boolean
    workdir?: string | null
    authRequired?: boolean
  }
  actionButton: {
    label: string
    // ⚠ Returns an error sentence, or null when the write landed. On a sentence the form stays
    // open and shows it (app#373): it used to close after a failed save, and the reader lost what
    // they typed along with the reason.
    onClick: (data: ServiceFormData) => Promise<string | null>
  }
  onDelete?: () => Promise<string | null>
  onCancel: () => void
}

const DEFAULTS = {
  web: {
    title: 'Website',
    url: 'http://localhost:8080',
  },
  terminal: {
    title: 'Terminal',
    command: '',
  },
}

// The iPhone agent's two pages (nobgp-agent src/ios_files.go), each its own service: a shared
// photos link never opens the files.
const PHONE_PAGES = {
  files: {
    title: 'Phone files',
    url: 'http://127.0.0.1:47080',
    help: 'Files sent to this phone (Inbox) and from it (Outbox)',
  },
  photos: { title: 'Phone photos', url: 'http://127.0.0.1:47081', help: 'Recent photos shared by this phone' },
}
type PhonePage = keyof typeof PHONE_PAGES

function authRequiredForType(value: boolean | undefined, serviceType: ServiceType): boolean {
  // Terminals are always auth-required — public terminals are not supported; use sharing instead.
  if (serviceType === 'terminal') return true
  // ⚠ A new web service requires sign-in unless the reader turns it off (app#373). The default was
  // false, so a Member's first save of a web service asked for a PUBLIC proxy, which the router
  // refuses to anyone but an org Owner/Admin (PERMISSIONS.md § 5 Services).
  return value ?? true
}

export default function ServiceForm({
  nodeId,
  nodeName,
  title,
  serviceType,
  hideTypeChange,
  onTypeChange,
  serviceUrl,
  initialData,
  actionButton,
  onDelete,
  onCancel,
}: Readonly<ServiceFormProps>) {
  const [url, setUrl] = useState<string>(initialData?.url || '')
  const [serviceTitle, setServiceTitle] = useState<string>(initialData?.title || '')
  const [command, setCommand] = useState<string>(initialData?.command || '')
  const [admin, setAdmin] = useState<boolean>(initialData?.admin ?? false)
  const [workdir, setWorkdir] = useState<string>(initialData?.workdir || '')
  const [authRequired, setAuthRequired] = useState<boolean | undefined>(initialData?.authRequired)
  const effectiveAuthRequired = authRequiredForType(authRequired, serviceType)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  // A PUBLIC proxy is an org Owner/Admin decision (the router's ActionServiceUnauthenticated).
  // Anyone else sees the switch, locked on; a service that is already public stays as it is.
  const { can } = useOrgRole()
  const canMakePublic = can('service.unauthenticated')
  // A node with no account configured has only one identity to offer, the
  // superuser, so it refuses to run anything unelevated. A node too old to
  // report the account reads the same way here on purpose — either way we
  // cannot promise the service will run as anything lesser than root.
  const execUser = useNodeMetadata(state => state.nodeMetadata.find(m => m.nodeId === nodeId)?.execUser)
  // ⚠ An iPhone (agent GOOS ios) serves one thing: its shared folder of files and photos, the
  // agent's Phone Files page (nobgp-agent src/ios_files.go). iOS runs no command and no other
  // server, so the form has no type and no URL there.
  const phone = useNodeMetadata(state => state.nodeMetadata.find(m => m.nodeId === nodeId)?.os) === 'ios'
  const [phonePage, setPhonePage] = useState<PhonePage>(
    initialData?.url === PHONE_PAGES.photos.url ? 'photos' : 'files'
  )
  const page = PHONE_PAGES[phonePage]
  const adminRequired = serviceType === 'terminal' && !execUser
  const effectiveAdmin = admin || adminRequired

  const handleSubmit = async () => {
    setSubmitting(true)

    const formData: ServiceFormData = {
      title: serviceTitle.trim() || (phone ? page.title : DEFAULTS[serviceType].title),
      url: phone ? page.url : serviceType === 'web' ? url || DEFAULTS[serviceType].url : null,
      command: serviceType === 'terminal' ? command.trim() || null : null,
      admin: serviceType === 'terminal' ? effectiveAdmin : false,
      workdir: serviceType === 'terminal' ? workdir.trim() || null : null,
      authRequired: effectiveAuthRequired,
    }

    setError(null)
    const failure = await actionButton.onClick(formData)
    setError(failure)
    setSubmitting(false)
  }

  const handleDelete = async () => {
    setSubmitting(true)
    setError(null)
    const failure = (await onDelete?.()) ?? null
    setError(failure)
    setSubmitting(false)
  }

  const helpText = phone
    ? `${page.help}, as a web page at ${effectiveAuthRequired ? 'an authenticated' : 'a'} public URL.`
    : effectiveAuthRequired
    ? `Exposed local ${serviceType === 'web' ? 'web application' : 'terminal command'} via an authenticated public URL.`
    : `Exposed local ${serviceType === 'web' ? 'web application' : 'terminal command'} via a public URL.`

  return (
    <Stack spacing={3}>
      <Stack direction="row" sx={{
        alignItems: 'center'
      }}>
        <Typography variant="subtitle1" color="secondary" sx={{
          flexGrow: 1
        }}>
          {title}
        </Typography>
        {nodeName && (
          <Tooltip title="Node name" placement="top" arrow>
            <Stack direction="row" spacing={1} sx={{
              alignItems: 'center'
            }}>
              <Typography variant="code">{nodeName}</Typography>
              <IconAgent width={20} height={20} />
            </Stack>
          </Tooltip>
        )}
      </Stack>
      <Box>
        {serviceUrl && <Typography variant="subtitle1">{serviceUrl}</Typography>}
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          {helpText}
        </Typography>
      </Box>
      <Stack spacing={2}>
        {!hideTypeChange && phone && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Share
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              color="secondary"
              value={phonePage}
              onChange={(_, value) => value && setPhonePage(value)}
            >
              <ToggleButton value="files" sx={{ gap: 1 }}>
                <LuFolder size={18} />
                Files
              </ToggleButton>
              <ToggleButton value="photos" sx={{ gap: 1 }}>
                <LuImage size={18} />
                Photos
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        {!hideTypeChange && !phone && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Service Type
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              color="secondary"
              value={serviceType}
              onChange={(_, value) => value && onTypeChange?.(value)}
            >
              <ToggleButton value="web" sx={{ gap: 1 }}>
                <LuArrowUpRight size={18} />
                Web Service
              </ToggleButton>
              <ToggleButton value="terminal" sx={{ gap: 1 }}>
                <LuTerminal size={18} />
                Terminal Command
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <TextFieldForm
          label="Name"
          fallback={phone ? page.title : DEFAULTS[serviceType].title}
          value={serviceTitle}
          onChange={setServiceTitle}
        />

        {serviceType === 'web' && !phone && (
          <TextFieldForm
            lowercase
            submitOnValid
            label="Local URL"
            fallback={DEFAULTS[serviceType].url}
            filter={REGEX_VALID_URL_CHARACTERS}
            valid={REGEX_VALID_URL}
            value={url}
            onSubmit={setUrl}
            helperText="ie: http://localhost:8080, or https://myservice.local:3000"
          />
        )}

        {serviceType === 'terminal' && (
          <CollapseSection subtitle="Advanced" defaultExpanded={(initialData?.admin ?? false) || adminRequired}>
            <Stack spacing={2}>
              <FormControlLabel
                slotProps={{ typography: { sx: { width: '100%' } } }}
                control={
                  <ColorSwitch
                    color="secondary"
                    checked={effectiveAdmin}
                    disabled={adminRequired}
                    onChange={e => setAdmin(e.target.checked)}
                  />
                }
                label={
                  <>
                    <Typography variant="body1">Run as root</Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', opacity: 0.7, color: adminRequired ? 'warning.main' : undefined }}>
                      {adminRequired
                        ? 'This node has no account configured for work without root, so this service has ' +
                          'to run as root (administrator on Windows). To change that, configure one on the ' +
                          'node itself with nobgp config --user <account>.'
                        : admin
                          ? 'Runs as root (administrator on Windows). A node whose owner set ' +
                            'allow-root: false will refuse the session.'
                          : `Runs as ${execUser}, the account configured on the node.`}
                    </Typography>
                  </>
                }
              />
              <TextFieldForm
                lowercase
                submitOnValid
                label="Command"
                value={command}
                onSubmit={setCommand}
                placeholder="login shell"
                helperText="Shell or executable to run when the terminal opens (e.g. bash, zsh, python, postgres). Leave blank to use the user's default login shell."
              />
              <TextFieldForm
                label="Working Directory"
                value={workdir}
                onChange={setWorkdir}
                placeholder="home directory"
                helperText="Working directory for the terminal session. Leave blank to use the user's home directory."
              />
            </Stack>
          </CollapseSection>
        )}

        {serviceType !== 'terminal' && (
          <FormControlLabel
            slotProps={{ typography: { sx: { width: '100%' } } }}
            control={
              <ColorSwitch
                color="secondary"
                checked={effectiveAuthRequired}
                disabled={!canMakePublic && effectiveAuthRequired}
                onChange={e => setAuthRequired(e.target.checked)}
              />
            }
            label={
              <>
                <Typography variant="body1">Require Authentication</Typography>
                {!canMakePublic && effectiveAuthRequired && (
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>
                    Only an owner or admin of the organization can make a service public.
                  </Typography>
                )}
                {!effectiveAuthRequired && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'warning.main',
                      position: 'absolute'
                    }}>
                    <em>
                      <strong>Public service</strong> — anyone with the URL can access it.
                    </em>
                  </Typography>
                )}
              </>
            }
          />
        )}

        {error && <Alert severity="error">{error}</Alert>}

        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            gap: 1,
            color: 'text.disabled'
          }}>
          {!!onDelete && (
            <Box sx={{ flexGrow: 1 }}>
              <DeleteConfirm message="This will permanently delete this service." onConfirm={handleDelete}>
                <Button variant="text" color="inherit" startIcon={<LuTrash />} disabled={submitting}>
                  Delete
                </Button>
              </DeleteConfirm>
            </Box>
          )}
          <Button variant="text" color="inherit" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={submitting}>
            {actionButton.label}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}
